import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from "react";
import { asyncTaskResult, asyncTasksListing, sitesApi } from "@/network/network.jsx";
import { ASYNC_TASK_STATUS, ASYNC_TASK_TYPES } from "@/other/constants/OtherKeys.jsx";
import { shouldShowAsyncDialog } from "@/bryntum-lib/editors/AsyncTaskMessageEditor/SwivuAsyncMessagePopup";
import { EDITOR_KEYS } from "@/other/constants/EditorKeys.jsx";
import { STORE_KEYS } from "@/other/constants/StoreKeys.jsx";
import { GRANTS } from "@/other/constants/PrivilegeTypes.jsx";
import { showToast } from "@/other/utils/utils.jsx";
import { Site } from "@/bryntum-lib/models/Site";
import { MarketingPage } from "@/bryntum-lib/models/MarketingPage";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import {
  ensureSiteInStore,
  findSiteForBoard,
  siteNeedsPersist,
} from "../utils";

const POLL_INTERVAL_MS = 10_000;

type PublishSnapshot = {
  isPublishing: boolean;
  isRepublishing: boolean;
};

let publishSnapshot: PublishSnapshot = { isPublishing: false, isRepublishing: false };
const publishListeners = new Set<() => void>();
let pollIntervalId: ReturnType<typeof setInterval> | null = null;
let pollSubscriberCount = 0;
let pollBoardId: number | undefined;
const publishTaskIdRef = { current: null as string | null };
const processedTaskIdsRef = { current: new Set<string>() };
const manualPublishRef = { current: false };

function emitPublishChange() {
  publishListeners.forEach((listener) => listener());
}

function setPublishSnapshot(next: Partial<PublishSnapshot>) {
  publishSnapshot = { ...publishSnapshot, ...next };
  emitPublishChange();
}

function subscribePublish(listener: () => void) {
  publishListeners.add(listener);
  return () => publishListeners.delete(listener);
}

function getPublishSnapshot() {
  return publishSnapshot;
}

function taskMatchesBoard(task: { meta_data?: { board_id?: number } }, boardId: number | undefined) {
  if (boardId == null) return false;
  return task.meta_data?.board_id === boardId;
}

async function pollPublishTasks(boardId: number | undefined) {
  if (!boardId) return;
  try {
    const res = await asyncTasksListing();
    const tasks: Array<{ id: number | string; type: number; status: string; meta_data?: { board_id?: number } }> =
      res.data ?? [];

    const publishTasks = tasks.filter(
      (t) => t.type === ASYNC_TASK_TYPES.SITE_PUBLISH && taskMatchesBoard(t, boardId),
    );

    let hasPending = false;

    for (const task of publishTasks) {
      const taskId = String(task.id);
      if (processedTaskIdsRef.current.has(taskId)) continue;

      if (task.status === ASYNC_TASK_STATUS.PENDING) {
        hasPending = true;
        publishTaskIdRef.current = taskId;
        setPublishSnapshot({
          isPublishing: true,
          isRepublishing: !manualPublishRef.current,
        });
        continue;
      }

      processedTaskIdsRef.current.add(taskId);

      if (publishTaskIdRef.current === taskId) {
        publishTaskIdRef.current = null;
        setPublishSnapshot({ isPublishing: false, isRepublishing: false });
        manualPublishRef.current = false;
      }

      if (task.status === ASYNC_TASK_STATUS.COMPLETED) {
        try {
          await asyncTaskResult(taskId);
        } catch {
          // loadsync may still refresh stores
        }
        showToast({ msg: "Site published successfully.", type: "success", duration: 4000 });
      } else if (task.status === ASYNC_TASK_STATUS.FAILED || task.status === ASYNC_TASK_STATUS.CANCELLED) {
        showToast({
          msg: task.status === ASYNC_TASK_STATUS.CANCELLED ? "Site publish cancelled." : "Site publish failed.",
          type: "error",
          duration: 4000,
        });
      }
    }

    if (!hasPending && publishTasks.every((t) => processedTaskIdsRef.current.has(String(t.id)))) {
      setPublishSnapshot({ isPublishing: false, isRepublishing: false });
      manualPublishRef.current = false;
    }
  } catch {
    // ignore transient poll errors
  }
}

function startPublishPoll(boardId: number | undefined) {
  pollBoardId = boardId;
  if (pollIntervalId != null) return;
  void pollPublishTasks(boardId);
  pollIntervalId = setInterval(() => void pollPublishTasks(boardId), POLL_INTERVAL_MS);
}

function stopPublishPoll() {
  if (pollIntervalId != null) {
    clearInterval(pollIntervalId);
    pollIntervalId = null;
  }
}

interface UseSiteOptions {
  initialSite?: Site | null;
  /** When true (SiteEditor), persist phantom site on mount. */
  syncOnMount?: boolean;
}

export function useSite(options: UseSiteOptions = {}) {
  const { initialSite, syncOnMount = false } = options;
  const { project, selectedBoard, user, editorHandler } = useSwivuBryntumContext();
  const boardSlug = (selectedBoard?.record as { slug?: string } | undefined)?.slug;
  const boardId = selectedBoard?.id as number | undefined;
  const isReadOnly = !user?.can?.(GRANTS.grant_four);

  const { version: pagesVersion } = useSwivuStore({ storeId: STORE_KEYS.marketingPageStore });
  const { version: sitesVersion } = useSwivuStore({ storeId: STORE_KEYS.sitesStore });

  const publishState = useSyncExternalStore(subscribePublish, getPublishSnapshot, getPublishSnapshot);

  const [activeSite, setActiveSite] = useState<Site | null>(initialSite ?? null);
  const [isEnsuring, setIsEnsuring] = useState(false);
  const [ensureError, setEnsureError] = useState<string | null>(null);

  const siteRecord = useMemo<Site | null>(() => {
    if (!project || !boardId) return null;
    const store = project.getCrudStore(STORE_KEYS.sitesStore);
    return findSiteForBoard(store, boardId);
  }, [project, sitesVersion, boardId]);

  const includedPages = useMemo(() => {
    if (!project) return [] as MarketingPage[];
    const store = project.getCrudStore(STORE_KEYS.marketingPageStore);
    const rows = (store?.query(() => true) as unknown as MarketingPage[]) ?? [];
    return rows.filter((p) => p.includeInSite);
  }, [project, pagesVersion]);

  const pagesMissingHtml = useMemo(
    () => includedPages.filter((p) => !p.url),
    [includedPages],
  );

  const includedPageCount = includedPages.length;

  const getOrCreateLocalSite = useCallback((): Site | null => {
    if (!project || !boardId) return null;
    const store = project.getCrudStore(STORE_KEYS.sitesStore);
    if (!store) return null;

    const existing =
      findSiteForBoard(store, boardId) ??
      (initialSite?.board === boardId ? initialSite : null);
    if (existing) return existing;

    const slug = boardSlug ?? "site";
    const added = store.add({
      board: boardId,
      slug,
      theme: { preset: "light" },
    }) as unknown as Site[];
    return added[0] ?? null;
  }, [project, boardId, boardSlug, initialSite]);

  const ensureSiteRecord = useCallback((): Site | null => {
    return getOrCreateLocalSite();
  }, [getOrCreateLocalSite]);

  const syncSiteIfNeeded = useCallback(async (): Promise<Site | null> => {
    if (!project || !boardId) {
      setEnsureError("Board is not ready yet. Close and reopen the editor.");
      return null;
    }

    const store = project.getCrudStore(STORE_KEYS.sitesStore);
    if (!store) {
      setEnsureError("Site store is unavailable.");
      return null;
    }

    const site = getOrCreateLocalSite();
    if (!site) {
      setEnsureError("Could not prepare site for this board.");
      return null;
    }

    if (!siteNeedsPersist(site)) {
      setActiveSite(site);
      setEnsureError(null);
      return site;
    }

    if (isReadOnly) {
      setActiveSite(site);
      setEnsureError("No site exists for this board yet.");
      return null;
    }

    setIsEnsuring(true);
    setEnsureError(null);
    try {
      ensureSiteInStore(store, site);
      await project.sync();

      const synced = findSiteForBoard(store, boardId);
      if (!synced || siteNeedsPersist(synced)) {
        throw new Error("Site sync did not persist");
      }

      setActiveSite(synced);
      return synced;
    } catch {
      setEnsureError("Could not create site for this board. Try again.");
      setActiveSite(site);
      return null;
    } finally {
      setIsEnsuring(false);
    }
  }, [project, boardId, getOrCreateLocalSite, isReadOnly]);

  useEffect(() => {
    if (!syncOnMount) return;
    void syncSiteIfNeeded();
  }, [syncOnMount, syncSiteIfNeeded]);

  useEffect(() => {
    if (!project || !boardId) return;
    const store = project.getCrudStore(STORE_KEYS.sitesStore);
    const synced = findSiteForBoard(store, boardId);
    if (!synced) return;
    if (!activeSite || siteNeedsPersist(activeSite) || synced.id === activeSite.id) {
      setActiveSite(synced);
    }
  }, [project, boardId, sitesVersion, activeSite]);

  useEffect(() => {
    if (!boardId) return undefined;
    pollSubscriberCount += 1;
    startPublishPoll(boardId);
    return () => {
      pollSubscriberCount -= 1;
      if (pollSubscriberCount === 0) {
        stopPublishPoll();
        pollBoardId = undefined;
      }
    };
  }, [boardId]);

  const startPublish = useCallback(async () => {
    if (!boardSlug) {
      showToast({ msg: "No board selected.", type: "error", duration: 3000 });
      return false;
    }
    manualPublishRef.current = true;
    setPublishSnapshot({ isRepublishing: false, isPublishing: true });
    try {
      const response = await sitesApi(boardSlug).publish();
      const ok =
        response?.status === 202 ||
        (response?.data && typeof response.data.id !== "undefined");
      if (!ok) {
        showToast({ msg: "Could not start site publish.", type: "error", duration: 3000 });
        setPublishSnapshot({ isPublishing: false, isRepublishing: false });
        manualPublishRef.current = false;
        return false;
      }
      publishTaskIdRef.current = String(response.data.id);
      if (shouldShowAsyncDialog("SITE_PUBLISH")) {
        (editorHandler as { openEditor: (...args: unknown[]) => void }).openEditor(
          EDITOR_KEYS.asyncTaskMessageEditor,
          null,
          { record: "SITE_PUBLISH" },
          { useExisting: true, by: "recordType" },
        );
      }
      if (pollBoardId !== boardId) {
        startPublishPoll(boardId);
      }
      return true;
    } catch {
      showToast({ msg: "Failed to start site publish.", type: "error", duration: 4000 });
      setPublishSnapshot({ isPublishing: false, isRepublishing: false });
      manualPublishRef.current = false;
      return false;
    }
  }, [boardSlug, boardId, editorHandler]);

  const siteReady = Boolean(
    activeSite && !siteNeedsPersist(activeSite) && !isEnsuring && !ensureError,
  );

  const publishBlockReason =
    includedPages.length === 0
      ? "Include at least one page on your site"
      : pagesMissingHtml.length > 0
        ? `${pagesMissingHtml.length} included page(s) need HTML`
        : null;

  const canPublish =
    siteReady && !publishState.isPublishing && !isReadOnly && includedPages.length > 0 && pagesMissingHtml.length === 0;

  return {
    activeSite,
    setActiveSite,
    siteRecord,
    ensureSiteRecord,
    isEnsuring,
    ensureError,
    syncSiteIfNeeded,
    isPublishing: publishState.isPublishing,
    isRepublishing: publishState.isRepublishing,
    startPublish,
    includedPages,
    pagesMissingHtml,
    includedPageCount,
    canPublish,
    publishBlockReason,
    siteReady,
    isReadOnly,
  };
}
